import {
    EpsonAlign,
    EpsonBarcode,
    EpsonColor,
    EpsonCut,
    EpsonFont,
    EpsonHRI,
    EpsonLevel,
    EpsonSize,
    EpsonSymbol,
    NFThermalPrinter
} from "src/app/shared/model/nf-thermal-printer.model";

export type PrintInstruction =
    | { type: 'text', content: string, style?: PrintStyle }
    | { type: 'align', value: EpsonAlign }
    | { type: 'size', width: number, height: number }
    | { type: 'style', bold?: boolean, underline?: boolean, reverse?: boolean, color?: EpsonColor }
    | { type: 'cut', feed?: boolean }
    | { type: 'logo' }
    | { type: 'barcode', value: string, format: EpsonBarcode, hri?: EpsonHRI, font?: EpsonFont, width?: number, height?: number }
    | { type: 'qrcode', value: string, size?: number }
    | { type: 'pulse' }
    | { type: 'newLine', times?: number };

export interface PrintStyle {
    bold?: boolean;
    underline?: boolean;
    reverse?: boolean;
    size?: number;
    align?: EpsonAlign;
    color?: EpsonColor;
}
export interface SimplifiedStyle {
    align?: EpsonAlign;
    bold?: boolean;
    underline?: boolean;
    reverse?: boolean;
    doubleWidth?: boolean;
    doubleHeight?: boolean;
    color?: EpsonColor;
}

export type SimplifiedLine =
    | { type: 'text'; content: string; style: SimplifiedStyle }
    | { type: 'barcode'; value: string; format: EpsonBarcode; hri?: EpsonHRI; font?: EpsonFont; width?: number; height?: number }
    | { type: 'qrcode'; value: string; size?: number }
    | { type: 'logo' }
    | { type: 'pulse' };

export type SimplifiedDocument = SimplifiedLine[];

export class PrintLayoutBuilder {
    private instructions: PrintInstruction[] = [];

    executeInstructionsOnDriver(drvObj: NFThermalPrinter) {
        for (const inst of this.instructions) {
            switch (inst.type) {
                case 'text':
                    if (inst.style) {
                        this.applyStyle(drvObj, inst.style);
                    }

                    drvObj.addText(inst.content);
                    break;
                case 'align':
                    drvObj.addTextAlign(inst.value);
                    break;
                case 'size':
                    drvObj.addTextSize(inst.width, inst.height);
                    break;
                case 'logo':
                    drvObj.addLogo();
                    break;
                case 'barcode':
                    drvObj.addBarcode(inst.value, inst.format, inst.hri, inst.font, inst.width, inst.height);
                    break;
                case 'newLine':
                    drvObj.addText('\n\r'.repeat(inst.times || 1));
                    break;
                case 'style':
                    this.applyStyle(drvObj, inst);
                    break;
                case 'cut':
                    drvObj.addCut(inst.feed ? EpsonCut.FEED : EpsonCut.NO_FEED);
                    break;
                case 'qrcode':
                    drvObj.addSymbol(inst.value, EpsonSymbol.QRCODE_MODEL_2, EpsonLevel.LEVEL_DEFAULT, inst.size || 8);
                    break;
                case 'pulse':
                    drvObj.addPulse();
                    break;

            }
        }

        return drvObj.send();
    }

    /**
     * Generates a simplified layout for fiscal printers (one command = one physical line).
     * Merges consecutive text fragments, removes spurious newlines, and applies the style at the start of the line.
     */
    getSimplifiedLayout(): SimplifiedDocument[] {
        const layout: SimplifiedDocument[] = [];
        let currentDoc: SimplifiedDocument = [];

        // Global style state (updated sequentially)
        let currentStyle: SimplifiedStyle = {
            align: EpsonAlign.LEFT,
            bold: false,
            underline: false,
            reverse: false,
            doubleWidth: false,
            doubleHeight: false,
            color: EpsonColor.COLOR_1
        };

        // Buffer for the current text line
        let pendingText: string | null = null;
        let lineStyle: SimplifiedStyle | null = null;

        const pushLine = (line: SimplifiedLine) => {
            currentDoc.push(line);
        };

        const ensureActiveDocument = () => {
            if (!layout.includes(currentDoc)) {
                layout.push(currentDoc);
            }
        };

        // Helper to merge global style with an optional local override
        const getActiveStyle = (localStyle?: PrintStyle): SimplifiedStyle => {
            let active = { ...currentStyle };
            if (localStyle) {
                active = {
                    ...active,
                    align: localStyle.align !== undefined ? localStyle.align : active.align,
                    bold: localStyle.bold !== undefined ? localStyle.bold : active.bold,
                    underline: localStyle.underline !== undefined ? localStyle.underline : active.underline,
                    reverse: localStyle.reverse !== undefined ? localStyle.reverse : active.reverse,
                    color: localStyle.color !== undefined ? localStyle.color : active.color,
                };
                if (localStyle.size !== undefined) {
                    const [dw, dh] = this.getDoubleFlags(localStyle.size);
                    active.doubleWidth = dw;
                    active.doubleHeight = dh;
                }
            }
            return active;
        };

        // Consolidates the text accumulated so far into a clean physical line
        const commitPendingText = () => {
            if (pendingText !== null) {
                const cleanContent = pendingText.replace(/[\r\n]/g, '');
                pushLine({
                    type: 'text',
                    content: cleanContent,
                    style: lineStyle || { ...currentStyle }
                });
                pendingText = null;
                lineStyle = null;
            }
        };

        for (const inst of this.instructions) {
            ensureActiveDocument();

            switch (inst.type) {
                case 'align':
                    currentStyle = { ...currentStyle, align: inst.value };
                    break;

                case 'size':
                    currentStyle = {
                        ...currentStyle,
                        doubleWidth: inst.width > 1,
                        doubleHeight: inst.height > 1
                    };
                    break;

                case 'style':
                    currentStyle = {
                        ...currentStyle,
                        bold: inst.bold !== undefined ? inst.bold : currentStyle.bold,
                        underline: inst.underline !== undefined ? inst.underline : currentStyle.underline,
                        reverse: inst.reverse !== undefined ? inst.reverse : currentStyle.reverse,
                        color: inst.color !== undefined ? inst.color : currentStyle.color
                    };
                    break;

                case 'text': {
                    // Normalize \r\n and \r to \n to handle any type of newline as a line break
                    const normalizedContent = inst.content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
                    const lines = normalizedContent.split('\n');

                    for (let i = 0; i < lines.length; i++) {
                        // Strictly remove any residual \r or \n characters from the fragment
                        const fragment = lines[i].replace(/[\r\n]/g, '');

                        if (i === 0) {
                            // First fragment: append to the text already present on the same line
                            if (pendingText === null) {
                                pendingText = fragment;
                                lineStyle = getActiveStyle(inst.style);
                            } else {
                                pendingText += fragment;
                            }
                        } else {
                            // Subsequent fragments imply a newline was encountered
                            commitPendingText();

                            if (i === lines.length - 1) {
                                // Last fragment of the split
                                if (fragment.length > 0) {
                                    pendingText = fragment;
                                    lineStyle = getActiveStyle(inst.style);
                                }
                            } else {
                                // Intermediate fragment
                                pendingText = fragment;
                                lineStyle = getActiveStyle(inst.style);
                                commitPendingText();
                            }
                        }
                    }
                    break;
                }

                case 'newLine': {
                    const times = inst.times || 1;
                    let remainingEmptyLines = times;

                    if (pendingText !== null) {
                        commitPendingText();
                        remainingEmptyLines--;
                    }

                    for (let i = 0; i < remainingEmptyLines; i++) {
                        pushLine({
                            type: 'text',
                            content: '',
                            style: { ...currentStyle }
                        });
                    }
                    break;
                }

                case 'barcode':
                    commitPendingText();
                    pushLine({
                        type: 'barcode',
                        value: inst.value.replace(/[\r\n]/g, ''), // Clean up potentially dirty barcode values
                        format: inst.format,
                        hri: inst.hri,
                        font: inst.font,
                        width: inst.width,
                        height: inst.height
                    });
                    break;

                case 'qrcode':
                    commitPendingText();
                    pushLine({
                        type: 'qrcode',
                        value: inst.value.replace(/[\r\n]/g, ''), // Clean up QR value
                        size: inst.size
                    });
                    break;

                case 'logo':
                    commitPendingText();
                    pushLine({ type: 'logo' });
                    break;

                case 'pulse':
                    commitPendingText();
                    pushLine({ type: 'pulse' });
                    break;

                case 'cut':
                    commitPendingText();
                    if (currentDoc.length > 0) {
                        currentDoc = [];
                    }
                    break;
            }
        }

        // Consolidate any remaining text at the end
        commitPendingText();
        
        return layout.filter(doc => doc.length > 0);
    }

    private getDoubleFlags(size: number): [boolean, boolean] {
        return [!!(size & EpsonSize.DOUBLE_WIDTH), !!(size & EpsonSize.DOUBLE_HEIGHT)];
    }

    private applyStyle(drvObj: NFThermalPrinter, style: PrintStyle) {
        if (style.align != null) {
            drvObj.addTextAlign(style.align);
        }

        if (style.bold != null || style.underline != null || style.reverse != null || style.color != null) {
            drvObj.addTextStyle(style.reverse, style.underline, style.bold, style.color);
        }

        if (style.size != null) {
            drvObj.addTextDouble(...this.getDoubleFlags(style.size));
        }
    }

    addText(content: string, style?: PrintStyle) {
        this.instructions.push({ type: 'text', content, style });
        return this;
    }

    addTextLine(content: string, style?: PrintStyle, spacing: number = 1) {
        this.addText(content, style)
            .newLine(spacing);

        return this;
    }

    setAlign(value: EpsonAlign) {
        this.instructions.push({ type: 'align', value });
        return this;
    }

    setSize(width: number, height: number) {
        this.instructions.push({ type: 'size', width, height });
        return this;
    }

    setStyle(style: PrintStyle) {
        this.instructions.push({ type: 'style', ...style });
        return this;
    }

    cut() {
        this.instructions.push({ type: 'cut', feed: true });
        return this;
    }

    addLogo() {
        this.instructions.push({ type: 'logo' });
        return this;
    }

    addBarcode(value: string, format: EpsonBarcode, hri?: EpsonHRI, font?: EpsonFont, width?: number, height?: number) {
        this.instructions.push({ type: 'barcode', value, format, hri, font, width, height });
        return this;
    }

    addQRCode(value: string, size?: number) {
        this.instructions.push({ type: 'qrcode', value, size });
        return this;
    }

    addPulse() {
        this.instructions.push({ type: 'pulse' });
        return this;
    }

    newLine(times = 1) {
        this.instructions.push({ type: 'newLine', times });
        return this;
    }

    isEmpty() {
        return this.instructions.length === 0;
    }

    getInstructions() {
        return this.instructions;
    }

    clear() {
        this.instructions = [];
        return this;
    }
}